import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { PdfWatermark } from './PdfWatermark';

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 10,
    fontFamily: 'Helvetica',
    position: 'relative'
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 20,
  },
  logo: {
    height: 80,
    width: 250,
    objectFit: 'contain'
  },
  infoContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20
  },
  boxInfo: {
    width: '48%',
    padding: 10,
    border: '1pt solid #ccc',
    borderRadius: 4,
    backgroundColor: '#f9f9f9',
  },
  boxTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 11,
    marginBottom: 6,
    color: '#333',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd',
    paddingBottom: 4
  },
  teamName: {
    fontSize: 12,
    marginBottom: 4,
    fontFamily: 'Helvetica-Bold'
  },
  textRow: {
    marginBottom: 3,
    color: '#333'
  },
  titleSection: {
    alignItems: 'center',
    marginBottom: 20,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: '#eee'
  },
  docHeaderInfo: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: 10
  },
  reportSubTitle: {
    fontSize: 16,
    fontFamily: 'Helvetica-Bold',
    marginTop: 5,
    marginBottom: 5,
    textAlign: 'center',
    color: '#333'
  },
  reportMeta: {
    fontSize: 10,
    color: '#444',
    marginBottom: 2
  },
  table: {
    display: "flex",
    width: "100%",
    marginTop: 10,
  },
  tableRowHeader: {
    display: 'flex',
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: '#000',
    paddingBottom: 6,
    marginBottom: 6
  },
  tableRow: {
    display: 'flex',
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    paddingVertical: 6,
  },
  colStatus: { width: "15%" },
  colType: { width: "15%" },
  colDesc: { width: "35%", paddingRight: 5 },
  colNotes: { width: "35%" },
  colHeaderFormat: { fontFamily: 'Helvetica-Bold' },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    fontSize: 8,
    color: '#888',
    textAlign: 'center',
    borderTopWidth: 1,
    borderTopColor: '#eee',
    paddingTop: 8
  }
});

const WorkOrderPdf = ({ workOrder, items, teamData, logoBase64, t, showWatermark = true }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {showWatermark && <PdfWatermark />}

        {/* Header - Centered Logo */}
        <View style={styles.headerRow}>
          {logoBase64 ? (
            <Image src={logoBase64} style={styles.logo} allowDangerousPaths />
          ) : (
            <Text style={{fontSize: 24, fontFamily: 'Helvetica-Bold', marginTop: 20}}>{teamData?.nomeTeam || 'Team Name'}</Text>
          )}
        </View>

        {/* Info Container */}
        <View style={styles.infoContainer}>
          {/* Team Box */}
          <View style={styles.boxInfo}>
            <Text style={styles.boxTitle}>Team / Fornitore</Text>
            <Text style={styles.teamName}>{teamData?.nomeTeam || 'Team Name'}</Text>
            <Text style={styles.textRow}>{teamData?.address || '-'}</Text>
            <Text style={styles.textRow}>{teamData?.city || '-'}</Text>
            <Text style={styles.textRow}>P.IVA / VAT: {teamData?.piva || 'N/A'}</Text>
          </View>

          {/* Vehicle Box */}
          <View style={styles.boxInfo}>
            <Text style={styles.boxTitle}>{t('detail.export_header_vehicle', 'Veicolo')}</Text>
            <Text style={styles.teamName}>{workOrder?.vehicleName || 'N/A'}</Text>
            <Text style={styles.textRow}>Status: {workOrder?.status || 'N/A'}</Text>
          </View>
        </View>

        {/* Title */}
        <View style={styles.titleSection}>
          <View style={styles.docHeaderInfo}>
            <Text style={styles.reportMeta}>{t('detail.export_header_order', 'Work Order')}: {workOrder?.title || 'N/A'}</Text>
            <Text style={styles.reportMeta}>
              {t('detail.export_header_date', 'Data')}: {workOrder?.createdAt ? format(workOrder.createdAt.toDate(), 'dd/MM/yyyy') : 'N/A'}
            </Text>
          </View>
          <Text style={styles.reportSubTitle}>{t('title', 'Work Order / Foglio Lavori')}</Text>
        </View>

        {/* Table */}
        <View style={styles.table}>
          <View style={styles.tableRowHeader}>
            <Text style={[styles.colStatus, styles.colHeaderFormat]}>{t('detail.export_col_status', 'Stato')}</Text>
            <Text style={[styles.colType, styles.colHeaderFormat]}>{t('detail.export_col_type', 'Origine')}</Text>
            <Text style={[styles.colDesc, styles.colHeaderFormat]}>{t('detail.export_col_task', 'Task')}</Text>
            <Text style={[styles.colNotes, styles.colHeaderFormat]}>{t('detail.export_col_notes', 'Note e Ricambi')}</Text>
          </View>
          
          {items.map((item, index) => {
            const partsString = Array.isArray(item.parts) && item.parts.length > 0 
                ? item.parts.map(p => `${p.quantity}x ${p.name}`).join(', ') 
                : '';
                
            return (
                <View style={styles.tableRow} key={index}>
                <Text style={styles.colStatus}>{item.status || 'Open'}</Text>
                <Text style={styles.colType}>{item.sourceParent ? `${item.source} (${item.sourceParent})` : item.source}</Text>
                <Text style={styles.colDesc}>{item.description}</Text>
                <View style={styles.colNotes}>
                    {item.notes ? <Text>{item.notes}</Text> : null}
                    {partsString ? <Text style={{ marginTop: 2, color: '#555' }}>[Parts: {partsString}]</Text> : null}
                    {item.hours ? <Text style={{ marginTop: 2, color: '#555' }}>[HH: {item.hours}]</Text> : null}
                </View>
                </View>
            );
          })}
        </View>

        {/* Footer */}
        <Text style={styles.footer} fixed render={({ pageNumber, totalPages }) => (
          `${teamData?.nomeTeam || 'Team'} - ${workOrder?.title || '-'} - ${workOrder?.createdAt ? format(workOrder.createdAt.toDate(), 'dd/MM/yyyy') : '-'} - ${workOrder?.vehicleName || '-'} - Pag ${pageNumber}/${totalPages}`
        )} />
        
      </Page>
    </Document>
  );
};

export default WorkOrderPdf;
